import React from 'react'
import * as rn from 'react-native'

import * as c from '../../common'

import ImageResizer from 'react-native-image-resizer'
import Orientation from 'react-native-orientation-locker'

export type CurrScreenRefType =
  | { scrollTo?(args: { animated: boolean; x: number; y: number }): void }
  | { scrollToOffset?(args: { animated: boolean; offset: number }): void }
  | undefined
export const currScreenRef = React.createRef<CurrScreenRefType>()
export let currScreenOffset: number = 0
export const setCurrScreenOffset = (offset: number) =>
  (currScreenOffset = offset)

interface ResizeImgOpts {
  readonly maxEdge?: number
  readonly quality?: number
  readonly rotation?: number
}

export const resizeImage = async (
  res: c.MobileFile,
  { maxEdge = 2048, quality = 100, rotation = 0 }: ResizeImgOpts = {},
): Promise<c.MobileFile> => {
  try {
    const isHorizontal = res.width > res.height

    const longEdge = isHorizontal ? res.width : res.height

    // Skipping the resize call entirely also skipped applying `rotation`,
    // since ImageResizer.createResizedImage() below is the only place that
    // happens - landscape photos (rotation !== 0) still need that call even
    // when they're already small enough that no resizing is needed.
    if (maxEdge >= longEdge && rotation === 0) {
      return res
    }

    const ratio = maxEdge >= longEdge ? 1 : maxEdge / longEdge
    const newWidth = Math.round(res.width * ratio)
    const newHeight = Math.round(res.height * ratio)

    const resCompress = await ImageResizer.createResizedImage(
      res.path,
      newWidth,
      newHeight,
      'JPEG',
      quality,
      rotation,
      undefined,
      false,
    )

    // Use the resizer's real output dimensions, not the pre-rotation
    // computed ones: a 90/-90 rotation swaps width and height, so the
    // computed newWidth/newHeight would report the wrong aspect ratio
    // downstream (media display).
    return {
      height: resCompress.height,
      mime: 'image/jpeg',
      path: resCompress.path,
      width: resCompress.width,
    }
  } catch (e) {
    throw new TypeError(`Unknown resize image ${e}`)
  }
}

// Weird cast?
export const emptyTextInput = (<rn.TextInput />) as unknown as rn.TextInput

export const useLayout = () => {
  const [layout, setLayout] = React.useState<rn.LayoutRectangle>({
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  })

  const handleLayoutEvent = React.useCallback(
    ({ nativeEvent: { layout } }: rn.LayoutChangeEvent): void => {
      setLayout(layout)
    },
    [],
  )

  return [layout, handleLayoutEvent] as const
}

/**
 * What a screen that locked the orientation should do on the way out (B134).
 *
 * `CCTVSimScreen` and `TakeMultiplePhotos` both called
 * `Orientation.lockToPortrait()` on unmount, and nothing anywhere in the repo
 * ever calls `unlockAllOrientations()`. On a phone that is correct and stays:
 * leaving the simulator while holding the device sideways must not strand the
 * rest of the app in landscape.
 *
 * On a tablet the same call strands it the other way. Jaron's IMG_5980.MOV
 * (2026-08-25) shows exactly that — he leaves a customer, the app flips to
 * portrait on its own with the iPad sitting on its keyboard, and rotating by
 * hand only appears to fix it, because the lock is still set and it comes
 * straight back.
 *
 * Deliberately NOT fixed by deleting the call, which regresses the phone, and
 * NOT by unlocking every orientation, which would let phone screens rotate into
 * layouts never built for them. The lock is a phone remedy, so it runs only on
 * a phone.
 *
 * Lives here rather than beside either caller (V62): both screens need the same
 * rule, and the next screen that locks the orientation should not have to
 * rediscover it. `react-utils` would have been the other candidate and is the
 * wrong one — the web imports it, and it currently pulls in nothing native.
 *
 * DEFERRED ONE TICK (2026-09-07, 12704974922 — freeze on exit holding the
 * phone sideways). `<OrientationLocker>` unmounts in the same commit as this
 * cleanup and removes itself from a module-level stack shared across every
 * `<OrientationLocker>` in the app (`react-native-orientation-locker/src/
 * OrientationLocker.js`), then re-applies whatever is left via its own
 * `setImmediate` — deliberately debounced so several lockers unmounting
 * together settle once, not once each. Calling `lockToPortrait()` synchronously
 * here fired a tick BEFORE that debounce resolved: two native rotation calls
 * landing back to back while the device is still physically mid-turn, which is
 * exactly the shape of freeze reported. Scheduling on the SAME primitive makes
 * this the last word instead of the first — deterministic order, not a race.
 * `lockToLandscape`/`lockToPortrait` only take the library's legacy KVC path
 * (SPEC B123), which is the piece already known to be fragile under exactly
 * this kind of back-to-back call.
 */
export const restoreOrientationOnLeave = (): void => {
  if (rn.Platform.OS === 'ios' && rn.Platform.isPad) return

  setImmediate(() => {
    Orientation.lockToPortrait()
  })
}
